import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QuoteSent = ({ brand, model }) => (
  <div data-testid="quote-sent" className="bg-[#2D6A4F]/20 border border-[#2D6A4F]/50 rounded-lg p-6 text-center">
    <CheckCircle size={32} className="text-[#6AB04C] mx-auto mb-3" />
    <p className="text-white font-semibold">Quote Request Sent!</p>
    <p className="text-[#8A8A8A] text-sm mt-1">
      We&apos;ll get back to you shortly with a quote for your {brand} {model}.
    </p>
  </div>
);

export default function QuoteForm({ selected, results, stage, selectedExtras }) {
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [quoteForm, setQuoteForm] = useState({ name: "", email: "", phone: "" });
  const [quoteSending, setQuoteSending] = useState(false);
  const [quoteSent, setQuoteSent] = useState(false);

  const sendQuote = async () => {
    setQuoteSending(true);
    try {
      await axios.post(`${API}/quote`, {
        ...quoteForm,
        brand: selected.brand, model: selected.model, generation: selected.generation,
        engine: results.engine,
        stage: stage === "stock" ? 0 : parseInt(stage),
        original_hp: results.original_hp, tuned_hp: results.tuned_hp, hp_gain: results.hp_gain,
        original_torque: results.original_torque, tuned_torque: results.tuned_torque, torque_gain: results.torque_gain,
        ecus: results.ecus || [],
        extras: selectedExtras,
      });
      setQuoteSent(true);
      setQuoteForm({ name: "", email: "", phone: "" });
    } catch {
      toast.error("Failed to send quote request. Please try again.");
    }
    setQuoteSending(false);
  };

  if (!quoteOpen) {
    return (
      <div className="mt-6 text-center">
        <Button
          data-testid="tuning-quote-btn"
          onClick={() => { setQuoteOpen(true); setQuoteSent(false); }}
          className="btn-primary px-8 py-5 text-sm font-semibold uppercase tracking-wider"
        >
          Request Quote for This Tune
        </Button>
      </div>
    );
  }

  if (quoteSent) {
    return (
      <div className="mt-6 text-center">
        <QuoteSent brand={selected.brand} model={selected.model} />
      </div>
    );
  }

  return (
    <div className="mt-6 text-center">
      <div data-testid="quote-form" className="bg-[#0A0A0A] rounded-lg p-6 border border-[#2A2A2A] text-left">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-white font-semibold text-sm uppercase tracking-wider">
            Request Quote — {selected.brand} {selected.model} {results.engine}
          </h4>
          <button onClick={() => setQuoteOpen(false)} className="text-[#8A8A8A] hover:text-white">
            <X size={18} />
          </button>
        </div>
        <div className="grid sm:grid-cols-3 gap-3 mb-4">
          <div>
            <label className="block text-xs text-[#8A8A8A] mb-1">Name *</label>
            <input data-testid="quote-name" type="text" value={quoteForm.name}
              onChange={(e) => setQuoteForm((p) => ({ ...p, name: e.target.value }))}
              className="w-full bg-[#1A1A1A] border border-[#2A2A2A] rounded-lg px-3 py-2.5 text-white text-sm focus:border-[#FF3B30] focus:outline-none"
              placeholder="Your name" />
          </div>
          <div>
            <label className="block text-xs text-[#8A8A8A] mb-1">Email *</label>
            <input data-testid="quote-email" type="email" value={quoteForm.email}
              onChange={(e) => setQuoteForm((p) => ({ ...p, email: e.target.value }))}
              className="w-full bg-[#1A1A1A] border border-[#2A2A2A] rounded-lg px-3 py-2.5 text-white text-sm focus:border-[#FF3B30] focus:outline-none"
              placeholder="your@email.com" />
          </div>
          <div>
            <label className="block text-xs text-[#8A8A8A] mb-1">Phone</label>
            <input data-testid="quote-phone" type="tel" value={quoteForm.phone}
              onChange={(e) => setQuoteForm((p) => ({ ...p, phone: e.target.value }))}
              className="w-full bg-[#1A1A1A] border border-[#2A2A2A] rounded-lg px-3 py-2.5 text-white text-sm focus:border-[#FF3B30] focus:outline-none"
              placeholder="+31 6 ..." />
          </div>
        </div>
        <Button data-testid="quote-submit-btn" disabled={quoteSending || !quoteForm.name || !quoteForm.email}
          onClick={sendQuote} className="w-full btn-primary py-3 text-sm font-semibold uppercase tracking-wider">
          {quoteSending ? "Sending..." : "Send Quote Request"}
        </Button>
      </div>
    </div>
  );
}
